/**
 * QActionChips Component (v3.0 - Bolt/Claude Inspired)
 * Compact action chips with tight spacing
 *
 * Features:
 * - 6 action chips with distinct colored icons
 * - Compact grid layout - tighter spacing
 * - Smaller icons and labels
 * - Professional Bolt.new style
 */

import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BoltIcon from '@mui/icons-material/Bolt';
import BarChartIcon from '@mui/icons-material/BarChart';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LightbulbIcon from '@mui/icons-material/Lightbulb';

/**
 * Action chip configurations matching the mockup
 * Each has: id, icon, label, color (for icon background)
 */
const ACTION_CHIPS = [
  {
    id: 'q_scan',
    icon: SearchIcon,
    label: 'Q Scan',
    color: '#27B56D', // Green
    prompt: 'Scan my trading activity and identify patterns in my performance.'
  },
  {
    id: 'q_signal',
    icon: BoltIcon,
    label: 'Q Signal',
    color: '#F59E0B', // Amber/Yellow
    prompt: "Show me today's top trading signals with entry, SL, and TP levels."
  },
  {
    id: 'q_analyze',
    icon: BarChartIcon,
    label: 'Q Analyze',
    color: '#6366F1', // Indigo/Purple
    prompt: 'Analyze my EA performance and give me detailed statistics.'
  },
  {
    id: 'q_risk',
    icon: GppMaybeIcon,
    label: 'Q Risk',
    color: '#EF4444', // Red
    prompt: 'Assess my current risk exposure and suggest optimal position sizing.'
  },
  {
    id: 'q_review',
    icon: TrendingUpIcon,
    label: 'Q Review',
    color: '#10B981', // Teal
    prompt: 'Review my trading history and tell me how to improve.'
  },
  {
    id: 'q_improve',
    icon: LightbulbIcon,
    label: 'Q Improve',
    color: '#8B5CF6', // Purple/Violet
    prompt: 'Give me personalized recommendations to improve my trading results.'
  }
];

/**
 * QActionChips Component
 * @param {function} onAction - Callback when chip is clicked (receives action object)
 * @param {boolean} disabled - Disable all chips
 * @param {string} variant - 'horizontal' (row) or 'grid' (2x3 grid for panel view)
 */
const QActionChips = ({ onAction, disabled = false, variant = 'horizontal' }) => {
  const handleClick = (action) => {
    if (!disabled && onAction) {
      onAction(action);
    }
  };

  const isGrid = variant === 'grid';

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: isGrid ? 'repeat(3, 1fr)' : 'repeat(auto-fit, minmax(100px, auto))',
        gap: isGrid ? 0.75 : 1, // Tighter gap (6px)
        maxWidth: isGrid ? '280px' : 'none', // Smaller max width
        justifyItems: isGrid ? 'center' : 'start'
      }}
    >
      {ACTION_CHIPS.map((action) => (
        <ActionChip
          key={action.id}
          action={action}
          onClick={() => handleClick(action)}
          disabled={disabled}
          variant={variant}
        />
      ))}
    </Box>
  );
};

/**
 * Individual Action Chip - Compact Bolt-style
 */
const ActionChip = ({ action, onClick, disabled, variant }) => {
  const IconComponent = action.icon;
  const isGrid = variant === 'grid';

  // Grid variant: compact vertical card style
  if (isGrid) {
    return (
      <Box
        onClick={disabled ? undefined : onClick}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          padding: '8px 6px', // Reduced padding
          backgroundColor: 'rgba(24, 24, 27, 0.6)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          borderRadius: '8px', // Smaller radius
          cursor: disabled ? 'default' : 'pointer',
          transition: 'all 0.12s ease',
          minWidth: '76px', // Smaller min width
          '&:hover': !disabled ? {
            backgroundColor: 'rgba(39, 39, 42, 0.8)',
            borderColor: `${action.color}40`
          } : {},
          '&:active': !disabled ? {
            transform: 'scale(0.98)'
          } : {},
          opacity: disabled ? 0.5 : 1
        }}
      >
        {/* Icon with colored background - Smaller */}
        <Box
          sx={{
            width: 28, // Smaller icon container
            height: 28,
            borderRadius: '6px',
            backgroundColor: action.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <IconComponent sx={{ color: '#FFFFFF', fontSize: '0.9375rem' }} />
        </Box>
        {/* Label - Smaller */}
        <Typography
          sx={{
            fontSize: '0.625rem', // Smaller font
            fontWeight: 500,
            color: '#A1A1AA',
            textAlign: 'center',
            lineHeight: 1.2
          }}
        >
          {action.label}
        </Typography>
      </Box>
    );
  }

  // Horizontal variant: compact pill chip style
  return (
    <Chip
      onClick={onClick}
      disabled={disabled}
      icon={
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: '5px',
            backgroundColor: action.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: '4px !important'
          }}
        >
          <IconComponent sx={{ color: '#FFFFFF', fontSize: '0.8125rem' }} />
        </Box>
      }
      label={
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#E4E4E7' }}>
          {action.label}
        </Typography>
      }
      sx={{
        backgroundColor: 'rgba(24, 24, 27, 0.6)',
        border: '1px solid rgba(255, 255, 255, 0.06)',
        borderRadius: '6px',
        height: 32, // Smaller height
        paddingRight: '8px',
        cursor: disabled ? 'default' : 'pointer',
        transition: 'all 0.12s ease',
        '&:hover': !disabled ? {
          backgroundColor: 'rgba(39, 39, 42, 0.8)',
          borderColor: `${action.color}40`
        } : {},
        '&:active': !disabled ? { transform: 'scale(0.98)' } : {},
        '&.Mui-disabled': { opacity: 0.5 },
        '& .MuiChip-icon': { marginRight: '4px' }
      }}
    />
  );
};

// Export the action chips config for use in useQChat
export { ACTION_CHIPS };
export default QActionChips;
